/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useMemo, useState } from 'react';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { EXPORT_OPTIONS, REPORT_TYPE } from 'modules/dashboard/constants';
import styles from 'modules/dashboard/components/tab-container/workbook-report/workbook-report.module.scss';
import VisualCard from 'modules/dashboard/components/tab-container/visual-card';
import GetAppIcon from '@mui/icons-material/GetApp';
import {
  selectCurrencyFilter,
  selectEnableHotelGroups,
  selectHotelName,
  selectResetBookingDate,
  selectTabList,
  selectVisualFilters,
  selectWorkbookDateType,
} from 'modules/dashboard/selectors';
import { formatDate, getVisualName } from 'modules/dashboard/functions';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import { AssistantIcon, Loader } from 'modules/common/components';
import { isEmpty } from 'modules/common/helpers/object';
import { toast } from 'react-toastify';
import TOAST_TYPES from 'modules/common/constants/toast-types';
import generateFilterString from 'modules/dashboard/utils/filters-transformation';
import * as XLSX from 'xlsx-js-style';
import { YEAR_MONTH_DAY_PATTERN } from 'modules/common/constants/date-range';
import WorkBookFilter from '../filter-panel';
import useWorkbookWidget from '../../hooks/use-workbook-widget';
import {
  selectPdfExportedObject,
  selectWorkbookFilterList,
  selectLoader,
  selectD1SetIsDisplay,
  selectD2SetIsDisplay,
  selectD3SetIsDisplay,
  selectWorkbookHotelVisual,
  selectHotelFocusOn,
  selectEnableSecondaryHotelGroups,
  selectWorkbookHotelComparison,
  selectWorkbookHotelFilterList,
  selectIsRatePlan,
  selectRatePlanVisuals,
  selectRatePlanFilterList,
  selectSymmetricComparisonToggle,
  selectIsDetailed,
  selectTravelAgentDetailsOnVisuals,
  selectTravelAgentDetailsOffVisuals,
  selectIsTravelAgent,
  selectIsRoomNightsPace,
  selectWorkbookAlignmentToggle,
  selectDateComparison,
  selectIsRoomType,
} from '../../selectors';
import { workbookActions } from '../../slice';
import { NotificationWindow } from './components';
import VisualGrid from '../../../visual-grid';
import { getDateSwitchSchema, getSymmetricComparisonSchema } from '../../../inner-filter/functions';
import MuiPaceVisual from './components/mui-pace-visual/mui-pace-visual';
import {
  PACE_TABULAR_TITLE,
  RATE_CATEGORY_TABULAR_TITLE,
  RATE_PLAN_TABULAR_TITLE,
  ROOM_TYPE_TABULAR_TITLE,
  TOAST_EXPORT_VISUALS_INCOMPLETE,
} from '../../constants';
import MuiRatePlanVisual from './components/mui-rate-plan-visual/mui-rate-plan-visual';
import { MUI_TABULAR_CONTAINER_STYLE } from '../../style';
import mapper from '../../../export-visual-data/mapper';
import MuiConsortiaVisual from './components/consortia-visual/mui-consortia-visual';
import ExportToCSV, { getPbiExportData } from '../../../export-visual-data/export-csv';
import ExportToExcel from '../../../export-visual-data/export-xlsx';
import { aoaToCsv, buildPBIExcelData } from '../../../export-visual-data/functions';
import { getHeaderFilterValues } from '../../function';
import CeruleanRateCategoryVisual from './components/rate-category/rate-category-visual';
import CeruleanRoomTypeVisual from './components/room-type/cerulean-room-type-visual';

/**
 * Workbook widget report with filters
 * @param {Number} value - Workbook widget tab value
 * @param {String} accessToken - Power BI access token
 * @param {Object} visuals - Workbook tab visual details
 * @param {Boolean} active - whether Workbook tab is active
 * @param {Date} latestDate - Latest booking date of selected hotel
 * @param {String} hotelTimezone - Timezone of selected hotel
 * @param {String} reportType - Report type of active tab
 * @returns
 */

const WorkbookReport = ({
  value,
  accessToken,
  visuals,
  active,
  latestDate,
  reportType,
  hotelTimezone,
  toggleDisabled,
}) => {
  const dispatch = useDispatch();
  const organizationId = useSelector(selectOrganizationId);
  const exportPdfDetails = useSelector(selectPdfExportedObject);
  const loading = useSelector(selectLoader);
  //
  useWorkbookWidget();
  // States are obtained from redux store. Refer each selector function for details.
  const selectedHotel = useSelector(selectHotelName);
  const hotelVisuals = useSelector(selectWorkbookHotelVisual);
  const ratePlanVisuals = useSelector(selectRatePlanVisuals);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const isWorkbookHotelComparison = useSelector(selectWorkbookHotelComparison);
  const isRatePlan = useSelector(selectIsRatePlan);
  const isRoomType = useSelector(selectIsRoomType);
  const ratePlanFilterList = useSelector(selectRatePlanFilterList);
  const workbookFilterList = useSelector(selectWorkbookFilterList);
  const workbookHotelFilterList = useSelector(selectWorkbookHotelFilterList);
  const d1SetIsDisplay = useSelector(selectD1SetIsDisplay);
  const d2SetIsDisplay = useSelector(selectD2SetIsDisplay);
  const d3SetIsDisplay = useSelector(selectD3SetIsDisplay);
  const currencyFilter = useSelector(selectCurrencyFilter);
  const isTravelAgentOn = useSelector(selectIsTravelAgent);
  const isDetailed = useSelector(selectIsDetailed);
  const travelAgentDetailsOnVisuals = useSelector(selectTravelAgentDetailsOnVisuals);
  const travelAgentDetailsOffVisuals = useSelector(selectTravelAgentDetailsOffVisuals);
  const isRoomNightsPace = useSelector(selectIsRoomNightsPace);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const dateComparison = useSelector(selectDateComparison);
  const alignmentToggle = useSelector(selectWorkbookAlignmentToggle);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const visualFilters = useSelector(selectVisualFilters);
  const selectedDateType = useSelector(selectWorkbookDateType);
  const symmetricComparisonToggle = useSelector(selectSymmetricComparisonToggle);
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);

  // Local states to set Workbook visual loading states
  const [isD1Loaded, setIsD1Loaded] = useState(false);
  const [isD2Loaded, setIsD2Loaded] = useState(false);
  const [isD3Loaded, setIsD3Loaded] = useState(false);
  const [isWorkbookLoaded, setIsWorkbookLoaded] = useState(false);
  const [isD1Rendered, setIsD1Rendered] = useState(false);
  const [isD2Rendered, setIsD2Rendered] = useState(false);
  const [isD3Rendered, setIsD3Rendered] = useState(false);
  //
  const [workbookReport, setWorkbookReport] = useState({});
  const [roomNightReport, setRoomNightReport] = useState({});
  const [rateCategoryReport, setRateCategoryReport] = useState({});
  //
  const [batchExport, setBatchExport] = useState(false);
  const [batchExportData, setBatchExportData] = useState({});
  const [pageName, setPageName] = useState('ReportSection75724c995dd4766cdd07');
  //
  const [isV1Processing, setIsV1Processing] = useState(false);
  const [isV2Processing, setIsV2Processing] = useState(false);
  const [isV3Processing, setIsV3Processing] = useState(false);
  // generate date switch filter
  const dateSwitchFilter = getDateSwitchSchema(visualFilters, selectedDateType?.id);
  // generate symmetric comparison filter
  const symmetricComparisonFilter = getSymmetricComparisonSchema(
    visualFilters,
    symmetricComparisonToggle
  );
  //
  const lastThreeFilterElements = [...workbookFilterList.slice(-3)];
  // To get visuals based on the configuration
  useEffect(() => {
    // To enable hotel comparison view based on selected hotels
    if (hotelFocusOn?.id !== selectedHotel?.id) {
      dispatch(workbookActions.setIsWorkbookHotelComparison(true));
    } else if (enableSecondaryHotelGroups && !enableHotelGroups) {
      dispatch(workbookActions.setIsWorkbookHotelComparison(true));
    } else if (!enableSecondaryHotelGroups && enableHotelGroups) {
      dispatch(workbookActions.setIsWorkbookHotelComparison(true));
    } else {
      dispatch(workbookActions.setIsWorkbookHotelComparison(false));
    }
  }, [enableHotelGroups, enableSecondaryHotelGroups, selectedHotel, hotelFocusOn]);
  // To get visual details based on index
  const getVisual = (index) => {
    let found;
    if (!isRatePlan && isTravelAgentOn && isDetailed) {
      found = travelAgentDetailsOnVisuals?.find((visual) => visual?.order === index);
    } else if (!isRatePlan && isTravelAgentOn && !isDetailed) {
      found = travelAgentDetailsOffVisuals?.find((visual) => visual?.order === index);
    } else if (isRatePlan) {
      found = ratePlanVisuals?.find((visual) => visual?.order === index);
    } else if (isWorkbookHotelComparison) {
      found = hotelVisuals?.find((visual) => visual?.order === index);
    } else {
      found = visuals?.find((visual) => visual?.order === index);
    }
    return found;
  };
  const getWidget = (
    index,
    cssClassName,
    setPBIReport = () => {},
    setIsLoaded = () => {},
    setIsRendered = () => {},
    displayFunction = () => {},
    filter = workbookFilterList
  ) => {
    const found = getVisual(index);
    return (
      found && (
        <VisualCard
          cssClassName={cssClassName}
          accessToken={accessToken}
          embedUrl={found?.embedUrl}
          id={found?.reportId}
          visualName={found?.name}
          pageName={found?.pageName}
          pageDisplayName={found?.pageDisplayName}
          reportName={found?.reportName}
          filters={filter}
          setPBIReport={(r) => setPBIReport(r)}
          setIsLoaded={(d) => setIsLoaded(d)}
          setIsRendered={(d) => setIsRendered(d)}
          reportType={REPORT_TYPE.WORKBOOK_REPORT}
          tab={TAB_VALUE.WORKBOOK_REPORT}
          dispatch={dispatch}
          displayFunction={displayFunction}
        />
      )
    );
  };
  //
  useEffect(() => {
    if (
      hotelFocusOn?.id !== selectedHotel?.id ||
      (enableSecondaryHotelGroups && !enableHotelGroups) ||
      (!enableSecondaryHotelGroups && enableHotelGroups)
    ) {
      setPageName('ReportSection2ddc6e9660c4801addc1');
    } else {
      setPageName('ReportSection75724c995dd4766cdd07');
    }
  }, [hotelFocusOn, selectedHotel, enableSecondaryHotelGroups, enableHotelGroups]);
  //
  const headerFiltersInfo = useMemo(
    () =>
      getHeaderFilterValues(
        workbookFilterList,
        resetBookingDate,
        dateComparison,
        alignmentToggle,
        selectedDateType?.id,
        symmetricComparisonToggle
      ),
    [
      workbookFilterList,
      resetBookingDate,
      dateComparison,
      alignmentToggle,
      selectedDateType,
      symmetricComparisonToggle,
    ]
  );
  //
  const exportReport = async () => {
    // Export the captured bookmark to PDF
    const capturedBookmark = await workbookReport.bookmarksManager.capture();
    //
    const settings = {
      format: 'PDF',
      powerBIReportConfiguration: {
        defaultBookmark: {
          state: capturedBookmark.state,
        },
        pages: [{ pageName }],
        reportLevelFilters: [
          {
            filter: generateFilterString([
              dateSwitchFilter,
              symmetricComparisonFilter,
              currencyFilter,
              ...lastThreeFilterElements,
            ]),
          },
        ],
      },
    };
    //
    dispatch(
      workbookActions.getWorkbookPdfExportUrl({
        settings,
        organizationId,
        query: 'isLLMScan=false',
        reportId: workbookReport.config.id,
        groupId: workbookReport.config.groupId,
        isWorkbookHotelComparisonEnable: isWorkbookHotelComparison,
        labels: isWorkbookHotelComparison
          ? {
              label1: `Selection Hotel ${
                selectedHotel && 'hotels' in selectedHotel ? 'Set' : ''
              } (A) : ${selectedHotel?.label}`,
              label2: `Comparison Hotel ${
                hotelFocusOn && 'hotels' in hotelFocusOn ? 'Set' : ''
              } (B) : ${hotelFocusOn?.label}`,
            }
          : {
              label1: `Hotel ${selectedHotel && 'hotels' in selectedHotel ? 'Set' : ''} : ${
                selectedHotel?.label
              }`,
            },
      })
    );
  };
  //
  useEffect(() => {
    if (exportPdfDetails && !isEmpty(workbookReport) && value === TAB_VALUE.WORKBOOK_REPORT) {
      toast(exportPdfDetails?.message, { type: TOAST_TYPES.SUCCESS });
    }
  }, [exportPdfDetails]);
  // rate plan, room type, room nights pace and consortia / iata views replace the power bi visuals
  // D1 - D3, there is no visual load to wait for
  const isTabularView = isRatePlan || isRoomType || isRoomNightsPace || isTravelAgentOn;
  //
  useEffect(() => {
    if (isTabularView) {
      setIsWorkbookLoaded(true);
      return;
    }
    // Calculate is Loaded  based on the given conditions
    const pair1 = d1SetIsDisplay ? d1SetIsDisplay && isD1Loaded : true;
    const pair2 = d2SetIsDisplay ? d2SetIsDisplay && isD2Loaded : true;
    const pair6 = d3SetIsDisplay ? d3SetIsDisplay && isD3Loaded : true;
    if (
      d1SetIsDisplay &&
      isD1Loaded &&
      d2SetIsDisplay &&
      isD2Loaded &&
      d3SetIsDisplay &&
      isD3Loaded
    ) {
      setIsWorkbookLoaded(true);
    } else {
      setIsWorkbookLoaded(pair1 || pair2 || pair6);
    }
  }, [
    d1SetIsDisplay,
    isD1Loaded,
    d2SetIsDisplay,
    isD2Loaded,
    d3SetIsDisplay,
    isD3Loaded,
    isTabularView,
  ]);
  // check batch excel export data and generate file
  useEffect(() => {
    // check if data for all expected visuals are available, if yes do the batch export
    if (Object.keys(batchExportData).length === 11) {
      const workbook = XLSX.utils.book_new();
      const orderedFileNames = Object.keys(batchExportData).sort(
        (a, b) => mapper[a].order - mapper[b].order
      );
      //
      orderedFileNames.forEach((sheet) => {
        XLSX.utils.book_append_sheet(
          workbook,
          batchExportData[sheet],
          mapper?.[sheet]?.title || sheet
        );
      });
      XLSX.writeFile(workbook, `Workbook_${formatDate(new Date(), YEAR_MONTH_DAY_PATTERN)}.xlsx`, {
        cellStyles: true,
      });
      setBatchExport(false);
      setBatchExportData({});
    }
  }, [batchExportData]);

  const exportBatchExcel = async () => {
    if (
      !isD1Rendered ||
      !isD2Rendered ||
      !isD3Rendered ||
      !d1SetIsDisplay ||
      !d2SetIsDisplay ||
      !d3SetIsDisplay
    ) {
      toast(TOAST_EXPORT_VISUALS_INCOMPLETE, {
        type: TOAST_TYPES.ERROR,
      });
      return;
    }
    setBatchExportData({});
    setBatchExport(true);
  };
  //
  const manageBatchExportData = (filename, csv) => {
    setBatchExportData((prev) => ({ ...prev, [filename]: csv }));
  };
  const getSummaryExportData = async () => {
    const data = await getPbiExportData({
      isLoaded: isD1Rendered,
      fileName: isWorkbookHotelComparison ? 'Summary' : 'Summary_Hotel',
      selectedHotel,
      report: workbookReport,
      visualTypes: ['pivotTable', 'donutChart', 'lineClusteredColumnComboChart'],
      metaData: headerFiltersInfo,
    });
    return data;
  };
  const getRoomNightExportData = async () => {
    const { aoa, workbookData } = await buildPBIExcelData({
      selectedHotel,
      report: roomNightReport,
      isInterPreterAgent: true,
      metaData: headerFiltersInfo,
    });
    return `${workbookData}${aoaToCsv(aoa)}`;
  };
  const getRateCategoryExportData = async () => {
    const data = await getPbiExportData({
      isLoaded: isD3Rendered,
      fileName: 'Rate_Category',
      selectedHotel,
      report: rateCategoryReport,
      metaData: headerFiltersInfo,
    });
    return data;
  };
  //
  return (
    active && (
      <Loader loading={loading}>
        <Grid
          container
          direction="row"
          className={styles.mainGrid}
          sx={{
            display: value === TAB_VALUE.WORKBOOK_REPORT ? '' : 'none',
          }}
        >
          <Grid
            item
            xs={2}
            cc={2.5}
            md={2}
            sx={{
              ml: {
                xs: 0,
                cc: 0.25,
                md: 0,
              },
            }}
          >
            <WorkBookFilter
              tabValue={value}
              reportType={reportType}
              visual={getWidget(100, styles.symmetricComparisonWindow)}
              visualName={getVisualName(visuals, 100)}
              latestDate={latestDate}
              hotelTimezone={hotelTimezone}
              isLoaded={isWorkbookLoaded}
              toggleDisabled={toggleDisabled}
            />
          </Grid>
          <Grid
            container
            item
            xs={10}
            cc={9.25}
            md={10}
            direction="column"
            justifyContent="center"
            alignItems="center"
          >
            <Grid
              mb={2}
              container
              item
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
            >
              {((isD1Loaded && isD2Loaded) || isD3Loaded) &&
                !isRatePlan &&
                !isRoomType &&
                !isTravelAgentOn &&
                !isRoomNightsPace && (
                  <>
                    <>
                      <Typography
                        sx={{
                          mr: 1,
                          color:
                            (d1SetIsDisplay && !isD1Rendered) ||
                            (d2SetIsDisplay && !isD2Rendered) ||
                            (d3SetIsDisplay && !isD3Rendered)
                              ? 'gray'
                              : 'black',
                        }}
                      >
                        Export Excel
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={exportBatchExcel}
                        disabled={
                          (d1SetIsDisplay && !isD1Rendered) ||
                          (d2SetIsDisplay && !isD2Rendered) ||
                          (d3SetIsDisplay && !isD3Rendered)
                        }
                        sx={{
                          '& .MuiSvgIcon-root': {
                            width: '1.5rem',
                            height: '1.25rem',
                            marginRight: 2,
                          },
                        }}
                      >
                        <GetAppIcon />
                      </IconButton>
                    </>
                    <>
                      <Typography
                        sx={{
                          mr: 1,
                          color:
                            (d1SetIsDisplay && !isD1Rendered) ||
                            (d2SetIsDisplay && !isD2Rendered) ||
                            (d3SetIsDisplay && !isD3Rendered)
                              ? 'gray'
                              : 'black',
                        }}
                      >
                        Export PDF
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={exportReport}
                        disabled={
                          (d1SetIsDisplay && !isD1Rendered) ||
                          (d2SetIsDisplay && !isD2Rendered) ||
                          (d3SetIsDisplay && !isD3Rendered)
                        }
                        sx={{
                          '& .MuiSvgIcon-root': {
                            width: '1.5rem',
                            height: '1.25rem',
                            marginRight: 2,
                          },
                        }}
                      >
                        <GetAppIcon />
                      </IconButton>
                    </>
                  </>
                )}
              {!isEmpty(ratePlanFilterList) &&
                !isEmpty(workbookHotelFilterList) &&
                !isEmpty(workbookFilterList) && <NotificationWindow />}
            </Grid>
            {isTravelAgentOn &&
              !isRatePlan &&
              !isRoomType &&
              isDetailed &&
              !isRoomNightsPace &&
              !isEmpty(ratePlanFilterList) && (
                <Box sx={MUI_TABULAR_CONTAINER_STYLE}>
                  <MuiConsortiaVisual
                    isDisplay={d1SetIsDisplay}
                    onClick={() => {
                      dispatch(workbookActions.setD1SetIsDisplay(!d1SetIsDisplay));
                    }}
                    style={styles.workbookVisualGrid}
                    enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                  />
                  {/* <VisualGrid
                    isDisplay={d1SetIsDisplay}
                    order={0}
                    visualsList={travelAgentDetailsOnVisuals}
                    visual={getWidget(
                      0,
                      styles.workbookReportD1RatePlan,
                      setWorkbookReport,
                      setIsD1Loaded,
                      setIsD1Rendered,
                      workbookActions.setD1SetIsDisplay,
                      ratePlanFilterList
                    )}
                    style={styles.workbookVisualGrid}
                    onClick={() => {
                      dispatch(workbookActions.setD1SetIsDisplay(!d1SetIsDisplay));
                    }}
                    toggleFragment={
                      isD1Rendered ? (
                        <ExportToExcel
                          report={workbookReport}
                          isLoaded={isD1Rendered}
                          fileName="IATA_Details_On"
                        />
                      ) : (
                        ''
                      )
                    }
                    interpreterFragment={
                      <AssistantIcon
                        visualName={getVisualName(visuals, 0)}
                        report={workbookReport}
                        isProcessing={isV1Processing}
                        setIsProcessing={setIsV1Processing}
                        disabled={!isD1Rendered || isV1Processing || !d1SetIsDisplay}
                        enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                        exportType ={exportType}
                        setExportType ={setExportType}
                      />
                    }
                  /> */}
                </Box>
              )}
            {isTravelAgentOn &&
              !isRatePlan &&
              !isRoomType &&
              !isDetailed &&
              !isRoomNightsPace &&
              !isEmpty(ratePlanFilterList) && (
                <Box sx={MUI_TABULAR_CONTAINER_STYLE}>
                  <MuiConsortiaVisual
                    isDisplay={d1SetIsDisplay}
                    onClick={() => {
                      dispatch(workbookActions.setD1SetIsDisplay(!d1SetIsDisplay));
                    }}
                    style={styles.workbookVisualGrid}
                    enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                  />
                  {/* <VisualGrid
                    isDisplay={d1SetIsDisplay}
                    order={0}
                    visualsList={travelAgentDetailsOffVisuals}
                    visual={getWidget(
                      0,
                      styles.workbookReportD1RatePlan,
                      setWorkbookReport,
                      setIsD1Loaded,
                      setIsD1Rendered,
                      workbookActions.setD1SetIsDisplay,
                      ratePlanFilterList
                    )}
                    style={styles.workbookVisualGrid}
                    onClick={() => {
                      dispatch(workbookActions.setD1SetIsDisplay(!d1SetIsDisplay));
                    }}
                    toggleFragment={
                      isD1Rendered ? (
                        <ExportToExcel
                          report={workbookReport}
                          isLoaded={isD1Rendered}
                          fileName="IATA_Details_Off"
                        />
                      ) : (
                        ''
                      )
                    }
                    interpreterFragment={
                      <AssistantIcon
                        visualName={getVisualName(visuals, 0)}
                        report={workbookReport}
                        isProcessing={isV1Processing}
                        setIsProcessing={setIsV1Processing}
                        disabled={!isD1Rendered || isV1Processing || !d1SetIsDisplay}
                        enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                        exportType ={exportType}
                        setExportType ={setExportType}
                      />
                    }
                  /> */}
                </Box>
              )}
            {/* TODO: Remove after JS Rate plan test */}
            {/* {isRatePlan && !isTravelAgentOn && !isEmpty(ratePlanFilterList) && (
              <Box sx={{ flexGrow: 1 }}>
                <VisualGrid
                  isDisplay={d1SetIsDisplay}
                  order={0}
                  visualsList={ratePlanVisuals}
                  visual={getWidget(
                    0,
                    styles.workbookReportD1RatePlan,
                    setWorkbookReport,
                    setIsD1Loaded,
                    setIsD1Rendered,
                    workbookActions.setD1SetIsDisplay,
                    ratePlanFilterList
                  )}
                  style={styles.workbookVisualGrid}
                  onClick={() => {
                    dispatch(workbookActions.setD1SetIsDisplay(!d1SetIsDisplay));
                  }}
                  toggleFragment={
                    isD1Rendered ? (
                      <ExportToExcel
                        report={workbookReport}
                        isLoaded={isD1Rendered}
                        fileName="Rate_Plan"
                      />
                    ) : (
                      ''
                    )
                  }
                />
              </Box>
            )} */}
            {!isRatePlan &&
            !isRoomType &&
            !isTravelAgentOn &&
            !isRoomNightsPace &&
            isWorkbookHotelComparison &&
            !isEmpty(workbookHotelFilterList) ? (
              <Box sx={{ flexGrow: 1 }}>
                <VisualGrid
                  isDisplay={d1SetIsDisplay}
                  order={0}
                  visualsList={hotelVisuals}
                  visual={getWidget(
                    0,
                    styles.workbookReportD1,
                    setWorkbookReport,
                    setIsD1Loaded,
                    setIsD1Rendered,
                    workbookActions.setD1SetIsDisplay,
                    workbookHotelFilterList
                  )}
                  style={styles.workbookVisualGrid}
                  onClick={() => {
                    dispatch(workbookActions.setD1SetIsDisplay(!d1SetIsDisplay));
                  }}
                  isWorkbookHotelComparison={isWorkbookHotelComparison}
                  comparisonHotel={hotelFocusOn}
                  displayHotelName
                  interpreterFragment={
                    <AssistantIcon
                      visualName={getVisualName(hotelVisuals, 0)}
                      report={workbookReport}
                      isProcessing={isV1Processing}
                      setIsProcessing={setIsV1Processing}
                      disabled={!isD1Rendered || isV1Processing || !d1SetIsDisplay}
                      enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                      getExcelExportData={getSummaryExportData}
                      exportType={exportType}
                      setExportType={setExportType}
                    />
                  }
                  toggleFragment={
                    <Box display="none">
                      <ExportToCSV
                        exportExcel
                        report={workbookReport}
                        isLoaded={isD1Rendered}
                        fileName="Summary_Hotel"
                        batchExport={batchExport}
                        setData={manageBatchExportData}
                        visualTypes={['pivotTable', 'donutChart', 'lineClusteredColumnComboChart']}
                        metaData={headerFiltersInfo}
                      />
                    </Box>
                  }
                />
                <VisualGrid
                  isDisplay={d3SetIsDisplay}
                  order={2}
                  visualsList={hotelVisuals}
                  visual={getWidget(
                    2,
                    styles.workbookReportD3,
                    setRateCategoryReport,
                    setIsD3Loaded,
                    setIsD3Rendered,
                    workbookActions.setD3SetIsDisplay,
                    workbookHotelFilterList
                  )}
                  style={styles.workbookVisualGrid}
                  onClick={() => {
                    dispatch(workbookActions.setD3SetIsDisplay(!d3SetIsDisplay));
                  }}
                  isWorkbookHotelComparison={isWorkbookHotelComparison}
                  comparisonHotel={hotelFocusOn}
                  interpreterFragment={
                    <AssistantIcon
                      visualName={getVisualName(hotelVisuals, 2)}
                      report={rateCategoryReport}
                      isProcessing={isV3Processing}
                      setIsProcessing={setIsV3Processing}
                      disabled={!isD3Rendered || isV3Processing || !d3SetIsDisplay}
                      enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                      getExcelExportData={getRateCategoryExportData}
                      exportType={exportType}
                      setExportType={setExportType}
                    />
                  }
                  toggleFragment={
                    <Box display="none">
                      <ExportToCSV
                        exportExcel
                        report={rateCategoryReport}
                        isLoaded={isD1Rendered}
                        fileName="Rate_Category_Hotel"
                        batchExport={batchExport}
                        setData={manageBatchExportData}
                        metaData={headerFiltersInfo}
                      />
                    </Box>
                  }
                />
                <VisualGrid
                  isDisplay={d2SetIsDisplay}
                  order={1}
                  visualsList={hotelVisuals}
                  visual={getWidget(
                    1,
                    styles.workbookReportD2,
                    setRoomNightReport,
                    setIsD2Loaded,
                    setIsD2Rendered,
                    workbookActions.setD2SetIsDisplay,
                    workbookHotelFilterList
                  )}
                  style={styles.workbookVisualGrid}
                  onClick={() => {
                    dispatch(workbookActions.setD2SetIsDisplay(!d2SetIsDisplay));
                  }}
                  isWorkbookHotelComparison={isWorkbookHotelComparison}
                  comparisonHotel={hotelFocusOn}
                  interpreterFragment={
                    <AssistantIcon
                      visualName={getVisualName(hotelVisuals, 1)}
                      report={roomNightReport}
                      isProcessing={isV2Processing}
                      setIsProcessing={setIsV2Processing}
                      disabled={!isD2Rendered || isV2Processing || !d2SetIsDisplay}
                      enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                      getExcelExportData={getRoomNightExportData}
                      exportType={exportType}
                      setExportType={setExportType}
                    />
                  }
                  toggleFragment={
                    <Box display="none">
                      <ExportToExcel
                        report={roomNightReport}
                        isLoaded={isD2Rendered}
                        fileName="Room_Night_Pace_Channel"
                        batchExport={batchExport}
                        setData={manageBatchExportData}
                        metaData={headerFiltersInfo}
                      />
                    </Box>
                  }
                />
              </Box>
            ) : (
              !isRatePlan &&
              !isRoomType &&
              !isTravelAgentOn &&
              !isRoomNightsPace &&
              !isEmpty(workbookFilterList) && (
                <Box sx={{ flexGrow: 1 }}>
                  <VisualGrid
                    isDisplay={d1SetIsDisplay}
                    order={0}
                    visualsList={visuals}
                    visual={getWidget(
                      0,
                      styles.workbookReportD1,
                      setWorkbookReport,
                      setIsD1Loaded,
                      setIsD1Rendered,
                      workbookActions.setD1SetIsDisplay
                    )}
                    style={styles.workbookVisualGrid}
                    onClick={() => {
                      dispatch(workbookActions.setD1SetIsDisplay(!d1SetIsDisplay));
                    }}
                    isWorkbookHotelComparison={isWorkbookHotelComparison}
                    comparisonHotel={hotelFocusOn}
                    displayHotelName
                    interpreterFragment={
                      <AssistantIcon
                        visualName={getVisualName(visuals, 0)}
                        report={workbookReport}
                        isProcessing={isV1Processing}
                        setIsProcessing={setIsV1Processing}
                        disabled={!isD1Rendered || isV1Processing || !d1SetIsDisplay}
                        enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                        getExcelExportData={getSummaryExportData}
                        exportType={exportType}
                        setExportType={setExportType}
                      />
                    }
                    toggleFragment={
                      <Box display="none">
                        <ExportToCSV
                          exportExcel
                          report={workbookReport}
                          isLoaded={isD1Rendered}
                          fileName="Summary"
                          batchExport={batchExport}
                          setData={manageBatchExportData}
                          visualTypes={[
                            'pivotTable',
                            'donutChart',
                            'lineClusteredColumnComboChart',
                          ]}
                          metaData={headerFiltersInfo}
                        />
                      </Box>
                    }
                  />
                  <VisualGrid
                    isDisplay={d3SetIsDisplay}
                    order={2}
                    visualsList={visuals}
                    visual={getWidget(
                      2,
                      styles.workbookReportD3,
                      setRateCategoryReport,
                      setIsD3Loaded,
                      setIsD3Rendered,
                      workbookActions.setD3SetIsDisplay
                    )}
                    style={styles.workbookVisualGrid}
                    onClick={() => {
                      dispatch(workbookActions.setD3SetIsDisplay(!d3SetIsDisplay));
                    }}
                    isWorkbookHotelComparison={isWorkbookHotelComparison}
                    comparisonHotel={hotelFocusOn}
                    interpreterFragment={
                      <AssistantIcon
                        visualName={getVisualName(visuals, 2)}
                        report={rateCategoryReport}
                        isProcessing={isV3Processing}
                        setIsProcessing={setIsV3Processing}
                        disabled={!isD3Rendered || isV3Processing || !d3SetIsDisplay}
                        enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                        getExcelExportData={getRateCategoryExportData}
                        exportType={exportType}
                        setExportType={setExportType}
                      />
                    }
                    toggleFragment={
                      <Box display="none">
                        <ExportToCSV
                          exportExcel
                          report={rateCategoryReport}
                          isLoaded={isD1Rendered}
                          fileName="Rate_Category"
                          batchExport={batchExport}
                          setData={manageBatchExportData}
                          metaData={headerFiltersInfo}
                        />
                      </Box>
                    }
                  />
                  <VisualGrid
                    isDisplay={d2SetIsDisplay}
                    order={1}
                    visualsList={visuals}
                    visual={getWidget(
                      1,
                      styles.workbookReportD2,
                      setRoomNightReport,
                      setIsD2Loaded,
                      setIsD2Rendered,
                      workbookActions.setD2SetIsDisplay
                    )}
                    style={styles.workbookVisualGrid}
                    onClick={() => {
                      dispatch(workbookActions.setD2SetIsDisplay(!d2SetIsDisplay));
                    }}
                    isWorkbookHotelComparison={isWorkbookHotelComparison}
                    comparisonHotel={hotelFocusOn}
                    interpreterFragment={
                      <AssistantIcon
                        visualName={getVisualName(visuals, 1)}
                        report={roomNightReport}
                        isProcessing={isV2Processing}
                        setIsProcessing={setIsV2Processing}
                        disabled={!isD2Rendered || isV2Processing || !d2SetIsDisplay}
                        enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                        getExcelExportData={getRoomNightExportData}
                        exportType={exportType}
                        setExportType={setExportType}
                      />
                    }
                    toggleFragment={
                      <Box display="none">
                        <ExportToExcel
                          report={roomNightReport}
                          isLoaded={isD2Rendered}
                          fileName="Room_Night_Pace_Channel"
                          batchExport={batchExport}
                          setData={manageBatchExportData}
                          metaData={headerFiltersInfo}
                        />
                      </Box>
                    }
                  />
                </Box>
              )
            )}
            {isRoomNightsPace &&
              !isRatePlan &&
              !isRoomType &&
              !isTravelAgentOn &&
              !isEmpty(workbookFilterList) && (
                <Box sx={MUI_TABULAR_CONTAINER_STYLE}>
                  <MuiPaceVisual
                    isDisplay={d1SetIsDisplay}
                    onClick={() => {
                      dispatch(workbookActions.setD1SetIsDisplay(!d1SetIsDisplay));
                    }}
                    style={styles.workbookVisualGrid}
                    title={PACE_TABULAR_TITLE}
                    enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                  />
                </Box>
              )}
            {isRatePlan &&
              isDetailed &&
              !isTravelAgentOn &&
              !isRoomNightsPace &&
              !isEmpty(ratePlanFilterList) && (
                <Box sx={MUI_TABULAR_CONTAINER_STYLE}>
                  <MuiRatePlanVisual
                    isDisplay={d1SetIsDisplay}
                    onClick={() => {
                      dispatch(workbookActions.setD1SetIsDisplay(!d1SetIsDisplay));
                    }}
                    style={styles.workbookVisualGrid}
                    title={RATE_PLAN_TABULAR_TITLE}
                    enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                  />
                </Box>
              )}
            {isRatePlan && !isDetailed && !isRoomNightsPace && !isTravelAgentOn && (
              <Box sx={MUI_TABULAR_CONTAINER_STYLE}>
                <CeruleanRateCategoryVisual
                  isDisplay={d1SetIsDisplay}
                  onClick={() => {
                    dispatch(workbookActions.setD1SetIsDisplay(!d1SetIsDisplay));
                  }}
                  style={styles.workbookVisualGrid}
                  title={RATE_CATEGORY_TABULAR_TITLE}
                  enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                />
              </Box>
            )}
            {isRoomType && !isRatePlan && !isRoomNightsPace && !isTravelAgentOn && (
              <Box sx={MUI_TABULAR_CONTAINER_STYLE}>
                <CeruleanRoomTypeVisual
                  isDisplay={d1SetIsDisplay}
                  onClick={() => {
                    dispatch(workbookActions.setD1SetIsDisplay(!d1SetIsDisplay));
                  }}
                  style={styles.workbookVisualGrid}
                  title={ROOM_TYPE_TABULAR_TITLE}
                  enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Loader>
    )
  );
};
//
export default WorkbookReport;
